import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { impact } from "../data/portfolio";

const ease = [0.16, 1, 0.3, 1];

function ImpactMetric({ metric, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const offset = idx % 2 === 0 ? [40, -40] : [60, -20];
  const y = useTransform(scrollYProgress, [0, 1], offset);

  return (
    <motion.div
      ref={ref}
      style={{ y }}
      data-testid={`impact-metric-${idx}`}
      className="relative md:px-6 md:border-r md:last:border-r-0 border-line/5"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease, delay: idx * 0.08 }}
      >
        <div className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.04em] text-metal leading-none">
          {metric.value}
        </div>
        <p className="mt-4 font-body text-sm md:text-base text-ink-2 leading-snug max-w-[24ch]">
          {metric.label}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Impact() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgX = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0, 0.04, 0.04, 0]
  );
  const headerY = useTransform(scrollYProgress, [0, 1], [40, -20]);

  return (
    <section
      ref={ref}
      data-testid="impact-section"
      data-snap-page
      className="relative bg-surface-2 border-y border-line/5 h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ x: bgX, opacity: bgOpacity }}
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center font-display text-[22vw] md:text-[14vw] leading-none tracking-[-0.06em] text-ink select-none"
      >
        impact
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <motion.div style={{ y: headerY }} className="mb-12 md:mb-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
            className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2"
          >
            02 — Impact, in numbers
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-12 md:gap-y-0">
          {impact.map((m, i) => (
            <ImpactMetric key={m.label} metric={m} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
